import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { CrmActionOutcomeLeaderboardItem } from "./use-crm-action-outcome-leaderboard";

export interface CrmActionContractFailurePolicy {
  action: string;
  maxRetries?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
}

export interface CrmActionQueueMaterializationCapability {
  operation: string;
  endpoint: string;
  method: "POST";
  entityType: "task" | "person" | "deal" | "activity" | "company";
  readinessStatuses: CrmActionExecutionReadinessStatus[];
  successStatuses: Array<"created" | "updated" | "existing">;
  idempotencyKey: "decisionEventId";
  executionMode: "staff_gated_explicit";
  mutatesCrm: true;
  requiredInputPaths: string[];
  supportedInputPaths: string[];
  targetFallbackPaths?: string[];
  supportedFields?: string[];
}

export interface CrmActionQueueManualResolutionPolicy {
  mode: "manual_only";
  reason: string;
  operatorAction: string;
  mutatesCrm: false;
  automatableFromQueue: false;
}

export type CrmActionExecutionReadinessStatus =
  | "ready_for_execution"
  | "manual_review"
  | "blocked"
  | "unknown";

export interface CrmActionContract {
  ref: string;
  domain: string;
  name: string;
  version: number;
  description: string;
  visibility: string;
  aiClassification: "none" | "limited" | "high";
  confirmationRequired: boolean;
  requiresHumanReview: boolean;
  materializableFromQueue: boolean;
  queueMaterialization: CrmActionQueueMaterializationCapability | null;
  manualOnlyFromQueue: boolean;
  queueManualResolution: CrmActionQueueManualResolutionPolicy | null;
  failurePolicy: CrmActionContractFailurePolicy;
  examplesCount: number;
  observedOutcomes: CrmActionOutcomeLeaderboardItem[];
  queue: CrmActionContractQueueSummary;
}

export interface CrmActionContractQueueSummary {
  reviewed: number;
  accepted: number;
  dismissed: number;
  readyForExecution: number;
  manualReview: number;
  blocked: number;
  unknown: number;
  materialized: number;
  crmMaterialized: number;
  manualResolved: number;
  materializableReady: number;
  manualOnlyReady: number;
  bridgeBlocked: number;
  inputBlockedReady: number;
  alreadyMaterialized: number;
  lastAcceptedAt: string | null;
  lastMaterializedAt: string | null;
}

export interface CrmActionContractSummary {
  registered: number;
  materializableFromQueue: number;
  manualOnlyFromQueue: number;
  humanReviewGated: number;
  outcomeBacked: number;
  queueCoverageRate: number;
  queueHandledCoverageRate: number;
  outcomeCoverageRate: number;
  queueAccepted: number;
  queueReadyForExecution: number;
  queueMaterialized: number;
  queueCrmMaterialized: number;
  queueManualResolved: number;
  queueManualOnlyReady: number;
  queueBridgeBlocked: number;
  queueInputBlockedReady: number;
  queueAlreadyMaterialized: number;
}

export interface CrmActionContractsResponse {
  windowDays: number;
  filters: {
    sourceEventType: string | null;
    signalFamily: string | null;
  };
  summary: CrmActionContractSummary;
  contracts: CrmActionContract[];
}

export interface CrmActionContractsFilters {
  sourceEventType?: string;
  signalFamily?: string;
}

export function useCrmActionContracts(filters: CrmActionContractsFilters = {}) {
  const [data, setData] = useState<CrmActionContractsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmActionContractsResponse }>(
        `/crm/intelligence/action-recommendations/contracts${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load action contracts");
    } finally {
      setIsLoading(false);
    }
  }, [filters.signalFamily, filters.sourceEventType]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return { data, isLoading, error, refetch: fetchContracts };
}
